import { ELEVATION_NOISE_THRESHOLD_METERS, SMOOTHING_WINDOW_POINTS, haversine } from "./utils.js";

export function buildViewerProfile(pointGroups) {
  const points = [];
  const segments = [];
  let totalDistance = 0;
  let totalSeconds = 0;

  pointGroups.forEach((group) => {
    if (group.length < 2) {
      return;
    }

    if (!points.length) {
      points.push(withDistance(group[0], totalDistance));
    }

    for (let index = 1; index < group.length; index += 1) {
      const previous = group[index - 1];
      const current = group[index];
      const seconds = getSegmentSeconds(previous, current);

      const distance = haversine(previous, current);

      if (!Number.isFinite(distance) || distance <= 0) {
        continue;
      }

      const speed = seconds ? distance / (seconds / 3600) : null;

      const from = withDistance(previous, totalDistance);
      totalDistance += distance;
      if (seconds) {
        totalSeconds += seconds;
      }
      const to = withDistance(current, totalDistance);

      segments.push({
        from,
        to,
        startDistance: from.distance,
        endDistance: to.distance,
        distance,
        seconds,
        speed,
      });
      points.push(to);
    }
  });

  if (points.length < 2 || !segments.length || totalDistance <= 0) {
    throw new Error("The GPX file needs at least two track points with coordinates.");
  }

  const hasTime = segments.every((segment) => segment.seconds && segment.speed !== null);
  const elevationPoints = points.filter((point) => Number.isFinite(point.elevation));
  const hasElevation = elevationPoints.length >= 2;
  if (hasElevation) {
    smoothElevations(elevationPoints);
  }
  const elevations = hasElevation ? elevationPoints.map((point) => point.elevation) : [];
  const minElevation = hasElevation ? Math.min(...elevations) : null;
  const maxElevation = hasElevation ? Math.max(...elevations) : null;
  const elevationChange = hasElevation ? calculateElevationChange(elevationPoints) : null;

  return {
    points,
    elevationPoints,
    segments,
    hasTime,
    hasElevation,
    totalDistance,
    totalSeconds: hasTime ? totalSeconds : null,
    averageSpeed: hasTime ? totalDistance / (totalSeconds / 3600) : null,
    totalAscent: hasElevation ? elevationChange.totalAscent : null,
    totalDescent: hasElevation ? elevationChange.totalDescent : null,
    minElevation,
    maxElevation,
    kmMarkers: hasElevation ? buildKmMarkers(elevationPoints, totalDistance) : [],
    kmSplits: buildKmSplits(elevationPoints, segments, totalDistance, hasTime, hasElevation),
  };
}

export function interpolatePoint(points, distance) {
  if (distance <= points[0].distance) {
    return Object.assign({}, points[0], { distance });
  }

  const lastPoint = points[points.length - 1];
  if (distance >= lastPoint.distance) {
    return Object.assign({}, lastPoint, { distance });
  }

  for (let index = 1; index < points.length; index += 1) {
    const previous = points[index - 1];
    const current = points[index];
    if (current.distance >= distance) {
      const span = current.distance - previous.distance;
      const ratio = span > 0 ? (distance - previous.distance) / span : 0;
      return {
        latitude: previous.latitude + (current.latitude - previous.latitude) * ratio,
        longitude: previous.longitude + (current.longitude - previous.longitude) * ratio,
        elevation: interpolateNullableValue(previous.elevation, current.elevation, ratio),
        smoothedElevation: interpolateNullableValue(previous.smoothedElevation, current.smoothedElevation, ratio),
        distance,
      };
    }
  }

  return Object.assign({}, lastPoint, { distance });
}

function buildKmMarkers(points, totalDistance) {
  const markers = [];
  const maxKm = Math.floor(totalDistance);

  for (let km = 1; km <= maxKm; km += 1) {
    markers.push({
      km,
      distance: km,
      elevation: interpolatePoint(points, km).smoothedElevation,
    });
  }

  return markers;
}

function buildKmSplits(points, segments, totalDistance, hasTime, hasElevation) {
  const splits = [];
  const splitCount = Math.ceil(totalDistance);

  for (let index = 0; index < splitCount; index += 1) {
    const startDistance = index;
    const endDistance = Math.min(index + 1, totalDistance);
    if (endDistance - startDistance <= 0.001) {
      continue;
    }

    const elevationChange = hasElevation ? calculateElevationChange(slicePointsByDistance(points, startDistance, endDistance)) : null;
    const split = {
      index: index + 1,
      startDistance,
      endDistance,
      distance: endDistance - startDistance,
      seconds: hasTime ? 0 : null,
      averageSpeed: null,
      totalAscent: hasElevation ? elevationChange.totalAscent : null,
      totalDescent: hasElevation ? elevationChange.totalDescent : null,
      pathSegments: [],
    };

    segments.forEach((segment) => {
      if (segment.distance <= 0) {
        return;
      }

      const overlapStart = Math.max(startDistance, segment.startDistance);
      const overlapEnd = Math.min(endDistance, segment.endDistance);
      if (overlapEnd <= overlapStart) {
        return;
      }

      const overlapDistance = overlapEnd - overlapStart;
      const startRatio = (overlapStart - segment.startDistance) / segment.distance;
      const endRatio = (overlapEnd - segment.startDistance) / segment.distance;
      if (hasTime) {
        split.seconds += segment.seconds * (overlapDistance / segment.distance);
      }
      split.pathSegments.push([
        pointToLatLng(interpolateSegmentPoint(segment, startRatio)),
        pointToLatLng(interpolateSegmentPoint(segment, endRatio)),
      ]);
    });

    split.averageSpeed = hasTime && split.seconds > 0 ? split.distance / (split.seconds / 3600) : null;
    splits.push(split);
  }

  return splits;
}

function smoothElevations(points) {
  points.forEach((point, index) => {
    if (points.length < SMOOTHING_WINDOW_POINTS || index < 2 || index > points.length - 3) {
      point.smoothedElevation = point.elevation;
      return;
    }

    const previousTwo = points[index - 2].elevation;
    const previousOne = points[index - 1].elevation;
    const current = point.elevation;
    const nextOne = points[index + 1].elevation;
    const nextTwo = points[index + 2].elevation;

    point.smoothedElevation = (-3 * previousTwo + 12 * previousOne + 17 * current + 12 * nextOne - 3 * nextTwo) / 35;
  });

  return points;
}

function calculateElevationChange(points) {
  const threshold = ELEVATION_NOISE_THRESHOLD_METERS;
  let totalAscent = 0;
  let totalDescent = 0;
  let trend = 0;
  let anchorElevation = points[0].smoothedElevation;
  let extremeElevation = anchorElevation;

  points.slice(1).forEach((point) => {
    const elevation = point.smoothedElevation;

    if (trend === 0) {
      const diff = elevation - anchorElevation;
      if (Math.abs(diff) >= threshold) {
        trend = diff > 0 ? 1 : -1;
        extremeElevation = elevation;
      }
      return;
    }

    if (trend > 0) {
      if (elevation > extremeElevation) {
        extremeElevation = elevation;
        return;
      }

      if (extremeElevation - elevation >= threshold) {
        totalAscent += extremeElevation - anchorElevation;
        trend = -1;
        anchorElevation = extremeElevation;
        extremeElevation = elevation;
      }
      return;
    }

    if (elevation < extremeElevation) {
      extremeElevation = elevation;
      return;
    }

    if (elevation - extremeElevation >= threshold) {
      totalDescent += anchorElevation - extremeElevation;
      trend = 1;
      anchorElevation = extremeElevation;
      extremeElevation = elevation;
    }
  });

  if (trend > 0 && extremeElevation - anchorElevation >= threshold) {
    totalAscent += extremeElevation - anchorElevation;
  }

  if (trend < 0 && anchorElevation - extremeElevation >= threshold) {
    totalDescent += anchorElevation - extremeElevation;
  }

  return { totalAscent, totalDescent };
}

function slicePointsByDistance(points, startDistance, endDistance) {
  const segmentPoints = [interpolatePoint(points, startDistance)];

  points.forEach((point) => {
    if (point.distance > startDistance && point.distance < endDistance) {
      segmentPoints.push(Object.assign({}, point));
    }
  });

  segmentPoints.push(interpolatePoint(points, endDistance));
  return segmentPoints;
}

function interpolateSegmentPoint(segment, ratio) {
  return {
    latitude: segment.from.latitude + (segment.to.latitude - segment.from.latitude) * ratio,
    longitude: segment.from.longitude + (segment.to.longitude - segment.from.longitude) * ratio,
  };
}

function pointToLatLng(point) {
  return [point.latitude, point.longitude];
}

function withDistance(point, distance) {
  return Object.assign({}, point, { distance });
}

function getSegmentSeconds(previous, current) {
  if (!(previous.time instanceof Date) || !(current.time instanceof Date)) {
    return null;
  }

  const seconds = (current.time.getTime() - previous.time.getTime()) / 1000;
  return seconds > 0 ? seconds : null;
}

function interpolateNullableValue(previous, current, ratio) {
  if (!Number.isFinite(previous) || !Number.isFinite(current)) {
    return null;
  }

  return previous + (current - previous) * ratio;
}
